/**
 * Trusted workload-issuer registry — query + mutation hooks.
 * Tenant-scoped: /api/v1/tenants/{tenant_id}/agents/workload-issuers[/...].
 * The status change is step-up-gated on the backend (agents.manage), so the
 * mutation is wrapped in withStepUp.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  workloadIssuerSchema,
  workloadIssuersResponseSchema,
  type IssuerCategory,
  type IssuerStatus,
  type RegisterIssuerInput,
  type WorkloadIssuer,
  type WorkloadIssuersResponse,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/agents/workload-issuers`;
}

export async function fetchWorkloadIssuers(tenantID: string): Promise<WorkloadIssuersResponse> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return workloadIssuersResponseSchema.parse(raw);
}

export function useWorkloadIssuersQuery(tenantID: string) {
  return useQuery<WorkloadIssuersResponse, ApiError>({
    queryKey: queryKeys.workloadIssuers.list(tenantID),
    queryFn: () => fetchWorkloadIssuers(tenantID),
  });
}

/**
 * useRegisterIssuerMutation registers a new trusted workload issuer (the
 * root-of-trust that lets an agent runtime mint grants). Step-up-gated on the
 * backend (agents.manage), so wrapped in withStepUp. Sends only the fields the
 * operator set; the backend fills safe defaults (subject_claim, assurance_class,
 * and assurance class).
 */
export function useRegisterIssuerMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<WorkloadIssuer, ApiError, RegisterIssuerInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: {
            issuer: input.issuer,
            allowed_audiences: input.allowed_audiences,
            trust_mode: input.trust_mode,
            ...(input.trust_mode === "pinned_jwks"
              ? { pinned_jwks: input.pinned_jwks }
              : { jwks_uri: input.jwks_uri }),
            ...(input.subject_claim ? { subject_claim: input.subject_claim } : {}),
            // Omitted when the operator left it unclassified — the backend stores
            // NULL rather than inferring one.
            ...(input.category ? { category: input.category } : {}),
          },
        }).then((raw) => workloadIssuerSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workloadIssuers.all });
    },
  });
}

export interface SetIssuerStatusInput {
  issuer: string;
  status: IssuerStatus;
}

export interface SetIssuerCategoryInput {
  issuer: string;
  /** undefined clears the classification back to unclassified. */
  category: IssuerCategory | undefined;
}

/**
 * useSetIssuerCategoryMutation classifies (or re-classifies, or clears) a trusted
 * issuer for the non-human census.
 *
 * This changes NO trust: it cannot alter who is trusted, which audience is
 * accepted, or which claim binds a subject. It changes how the workloads federated
 * from this issuer are COUNTED. That is why it is the only editable field on a
 * surface where the trust fields are deliberately immutable after registration.
 *
 * Still step-up-gated, because it shares the agents.manage subtree with its
 * siblings — and because a reclassification silently moves every workload behind
 * the issuer between segments of a number a customer may have reported.
 *
 * `category` is always sent explicitly, including as "" to clear. The backend
 * rejects an ABSENT field precisely so a client that forgot it cannot silently
 * un-classify an issuer.
 */
export function useSetIssuerCategoryMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<WorkloadIssuer, ApiError, SetIssuerCategoryInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/category`, {
          method: "POST",
          body: { issuer: input.issuer, category: input.category ?? "" },
        }).then((raw) => workloadIssuerSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workloadIssuers.all });
      // The census counts workloads by the category of the issuer behind them, so a
      // reclassification moves rows between its segments. Without this the roster
      // would keep showing the old counts until its own cache expired, and the
      // operator would reasonably conclude the classification had not taken.
      qc.invalidateQueries({ queryKey: queryKeys.nhiCensus.all });
    },
  });
}

/**
 * useSetIssuerStatusMutation enables/disables a trusted issuer. Disabling REVOKES
 * mint trust (agents from that issuer can no longer mint — fail-closed); enabling
 * restores it. The issuer URI is the natural key (carried in the body, not the path,
 * since it's a URI).
 */
export function useSetIssuerStatusMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<WorkloadIssuer, ApiError, SetIssuerStatusInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/status`, {
          method: "POST",
          body: { issuer: input.issuer, status: input.status },
        }).then((raw) => workloadIssuerSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workloadIssuers.all });
    },
  });
}
